// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useState } from "react";
import "./DeliveriesTable.scss";

import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from "react-i18next";
import { useTable } from 'react-table';
import { format } from "date-fns";

// Importing logos
import photo_icon from "../../assets/icons/gray/image.png";
import notes_icon from '../../assets/icons/gray/notes.png';
import bales_icon from '../../assets/icons/lightblue/bales_stacked.png';
import warning_icon from '../../assets/icons/alert.png';
import label_icon from '../../assets/icons/label_green.png';

/**
 * Reusable DeliveriesTable component that can be used in different contexts
 */
function DeliveriesTable({ 
  deliveries = {}, 
  activeDelivery, 
  onSelectDelivery
}) {
  const { i18n, t } = useTranslation();
  const { currentUser, labelsets, logEventCustom, suppliers } = React.useContext(AuthContext);
  
  // State for sorting
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Define columns for the table - memoized to prevent recreating on every render
  const COLUMNS = useMemo(() => [
    {
      Header: " ",
      accessor: "flagged",
      Cell: ({ row }) => (
        <div className="table-icon-wrapper">
          {row.original?.claim_data?.claim_status === 'confirmed' && <img src={warning_icon} alt="Warning" />}
          {row.original.notes && row.original.notes !== "" && <img src={notes_icon} alt="Notes" />}
        </div>
      ),
      id: "flagged",
      disableSortBy: true
    },
    {
      Header: t("property_date"),
      accessor: "date",
      Cell: ({ row }) => {
        const date = row.original.date; 
        const dateObj = date && typeof date.toDate === 'function' ? date.toDate() : new Date(date);
        return (
          <div className="date-time">
            {format(dateObj, "dd.MM.yyyy")}
            <div className="time">{format(dateObj, "HH:mm")}</div>
          </div>
        );
      },
      id: "date"
    },
    {
      Header: t("property_supplier"),
      accessor: "supplier",
      Cell: ({ row }) => {
        let supplierName = suppliers?.[row.original.supplier_id]?.display_name ?? row.original.supplier_id;
        // HOTFIX: Show MOLE data when supplier name is not available
        if (!supplierName || supplierName === "") {
            supplierName = row.original?.MOLE_data?.model_eye_in_registered?.carrier_creditor_name;
        }
        return supplierName;
      },
      id: "supplier"
    },
    {
      Header: t("property_material"),
      accessor: "material",
      Cell: ({ row }) => labelsets?.['materials']?.['labels']?.[row.original?.base_labels?.materials]?.display_name ?? "",
      id: "material"
    },
    {
      Header: t("property_registered_bales"),
      accessor: "bales",
      Cell: ({ row }) => {
        let baleCount = 0;
        if (row.original?.bales) {
          baleCount = Object.keys(row.original.bales).length;
        }
        return (
          <div className="photo-property">
            <p>{baleCount}</p>
            <img src={bales_icon} alt="Bales" />
          </div>
        );
      },
      id: "bales"
    },
    {
      Header: t("property_photos"),
      accessor: "photos",
      Cell: ({ row }) => {
        let photoCount = 0;
        if (row.original?.delivery_images) {
          photoCount = Object.keys(row.original.delivery_images).length;
        }

        return (
          <div className="photo-property">
            <p>{photoCount}</p>
            <img src={photo_icon} alt="Photos" />
          </div>
        );
      },
      id: "photos"
    },
    // Conditionally add the Labels column for admin and labeler users
    ...(currentUser?.info?.user_flags && 
      ['admin', 'labeler'].some(flag => currentUser.info.user_flags.includes(flag)) ? [{
      Header: t("labels"),
      accessor: "labels",
      Cell: ({ row }) => {
        if (!row.original?.bales || Object.keys(row.original.bales).length === 0) return null;
        
        const labelCount = Object.values(row.original.bales).reduce(
          (acc, bale) => acc + (bale?.labelled ? 1 : 0), 0
        );
        
        if (labelCount === 0) return null;
        
        const labelPercentage = Math.floor(labelCount / Object.keys(row.original.bales).length * 100);
        
        return (
          <div className="photo-property">
            <p>{labelPercentage}%</p>
            <img src={label_icon} alt="Labels" />
          </div>
        );
      },
      id: "labels"
    }] : [])
  ], [t, labelsets, currentUser?.info?.user_flags]);

  // Process data for display and sorting
  const data = useMemo(() => {
    if (!deliveries) return [];
    
    return Array.isArray(deliveries) 
      ? deliveries 
      : Object.values(deliveries);
  }, [deliveries]);
  
  // Handle sorting
  const handleSort = (columnId) => {
    // Prevent unnecessary state updates
    if (columnId === 'flagged') return;
    
    setSortDirection(prevDirection => 
      sortColumn === columnId && prevDirection === "asc" ? "desc" : "asc"
    );
    setSortColumn(columnId);
  };
  
  // Get sorted data
  const sortedData = useMemo(() => {
    if (!data.length) return [];
    
    return [...data].sort((a, b) => {
      let aValue, bValue, result = 0;
      
      // Determine what to sort by based on column ID
      switch (sortColumn) {
        case "date":
          aValue = a.date && typeof a.date.toDate === 'function' ? a.date.toDate().getTime() : new Date(a.date).getTime();
          bValue = b.date && typeof b.date.toDate === 'function' ? b.date.toDate().getTime() : new Date(b.date).getTime();
          result = aValue - bValue;
          break;
          
        case "supplier":
          aValue = suppliers?.[a.supplier_id]?.display_name ?? a.supplier_id;
          bValue = suppliers?.[b.supplier_id]?.display_name ?? b.supplier_id;
          result = String(aValue).localeCompare(String(bValue));
          break;
          
        case "material":
          aValue = labelsets?.['materials']?.['labels']?.[a?.base_labels?.materials]?.display_name ?? "";
          bValue = labelsets?.['materials']?.['labels']?.[b?.base_labels?.materials]?.display_name ?? "";
          result = String(aValue).localeCompare(String(bValue));
          break;
          
        case "bales":
          aValue = a.bales ? Object.keys(a.bales).length : 0;
          bValue = b.bales ? Object.keys(b.bales).length : 0;
          result = aValue - bValue;
          break;
          
        case "photos":
          aValue = a.delivery_images ? Object.keys(a.delivery_images).length : 0;
          bValue = b.delivery_images ? Object.keys(b.delivery_images).length : 0;
          result = aValue - bValue;
          break;
          
        case "labels":
          // Calculate label percentage for comparison
          const getPercentage = (delivery) => {
            if (!delivery.bales || Object.keys(delivery.bales).length === 0) return 0;
            const labelCount = Object.values(delivery.bales).reduce(
              (acc, bale) => acc + (bale?.labelled ? 1 : 0), 0
            );
            return Math.floor(labelCount / Object.keys(delivery.bales).length * 100);
          };
          
          aValue = getPercentage(a);
          bValue = getPercentage(b);
          result = aValue - bValue;
          break;
          
        default:
          result = 0;
      }
      
      return sortDirection === "asc" ? result : -result;
    });
  }, [data, sortColumn, sortDirection, labelsets]);

  // Initialize table
  const tableInstance = useTable({ 
    columns: COLUMNS, 
    data: sortedData
  });
  
  const { 
    getTableProps, 
    getTableBodyProps, 
    headerGroups, 
    rows, 
    prepareRow 
  } = tableInstance;

  // Make sure we don't call during render to prevent infinite loops
  const handleSelectDelivery = (deliveryId) => {
    if (onSelectDelivery && deliveryId !== activeDelivery) {
      onSelectDelivery(deliveryId);
      
      // Log event if logging function is provided
      if (logEventCustom) {
        logEventCustom("select_content", { content_type: "delivery", content_id: deliveryId });
      }
    }
  };

  // Render sort icon based on current sort state
  const renderSortIcon = (columnId) => {
    if (sortColumn !== columnId) return null;
    
    return (
      <span className="sort-icon">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // Check if deliveries are empty - simplified to prevent unnecessary calculations
  const hasDeliveries = Boolean(
    deliveries && (
      Array.isArray(deliveries) ? deliveries.length > 0 : Object.keys(deliveries).length > 0
    )
  );

  if (!hasDeliveries) {
    return (
      <div className="deliveries-table-empty">
        <p>{t("no_deliveries")}</p>
      </div>
    );
  }

  return (
    <div className="deliveries-table">
      <div className="table-wrapper">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, idx) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                {headerGroup.headers.map((column, colIdx) => (
                  <th 
                    {...column.getHeaderProps()} 
                    key={colIdx}
                    onClick={() => column.id !== 'flagged' && handleSort(column.id)}
                  >
                    {column.render('Header')}
                    {renderSortIcon(column.id)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, idx) => {
              prepareRow(row);
              const isActive = row.original.id === activeDelivery;
              
              return (
                <tr 
                  {...row.getRowProps()} 
                  key={idx}
                  onClick={() => handleSelectDelivery(row.original.id)}
                  className={isActive ? "active-row" : undefined}
                >
                  {row.cells.map((cell, cellIdx) => (
                    <td {...cell.getCellProps()} key={cellIdx}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
            
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-8">
                  {t("no_deliveries_found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DeliveriesTable; 